import React, { useRef, useState } from "react";
import { Modal, Button, Form, ProgressBar } from "react-bootstrap";
import { toast } from "react-toastify";

const UploadBulkDataModal = ({ show, onClose, tenant }) => {
  const fileInputRef = useRef(null);

  const [dataType, setDataType] = useState("player");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Tenant ID comes from props
  const tenantId = tenant?.id;

  const allowedFileTypes = [".csv", ".xlsx", ".xls"];

  // --------------------------------------------------
  // DATA TYPE LABEL
  // --------------------------------------------------
  const getDataTypeLabel = (type) => {
    switch (type) {
      case "coach":
        return "Coach";

      case "player":
        return "Player";

      case "staff":
        return "Staff";

      default:
        return "";
    }
  };

  // --------------------------------------------------
  // FILE SELECT
  // --------------------------------------------------
  const handleFileSelect = (file) => {
    if (!file) return;

    const fileExtension = "." + file.name.split(".").pop().toLowerCase();

    // Validate extension
    if (!allowedFileTypes.includes(fileExtension)) {
      toast.error("Please upload a CSV or Excel file.", {
        position: "top-right",
        autoClose: 1500,
      });

      return;
    }

    // Validate size - 10 MB
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size should not exceed 10 MB.", {
        position: "top-right",
        autoClose: 1500,
      });

      return;
    }

    setSelectedFile(file);
    setUploadProgress(0);
  };

  // --------------------------------------------------
  // INPUT CHANGE
  // --------------------------------------------------
  const handleInputChange = (event) => {
    const file = event.target.files?.[0];

    handleFileSelect(file);
  };

  // --------------------------------------------------
  // DRAG DROP
  // --------------------------------------------------
  const handleDrop = (event) => {
    event.preventDefault();

    setIsDragging(false);

    const file = event.dataTransfer.files?.[0];

    handleFileSelect(file);
  };

  const handleDragOver = (event) => {
    event.preventDefault();

    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  // --------------------------------------------------
  // REMOVE FILE
  // --------------------------------------------------
  const removeFile = () => {
    setSelectedFile(null);
    setUploadProgress(0);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // --------------------------------------------------
  // DEMO UPLOAD
  // --------------------------------------------------
  const handleUpload = async () => {
    if (!tenantId) {
      toast.error("Tenant ID is missing.", {
        position: "top-right",
        autoClose: 1500,
      });

      return;
    }

    if (!selectedFile) {
      toast.warning("Please select a file first.", {
        position: "top-right",
        autoClose: 1500,
      });

      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);

      // --------------------------------------------------
      // DEMO FOR NOW
      // Replace this section with your API later.
      // --------------------------------------------------

      const formData = new FormData();

      formData.append("file", selectedFile);
      formData.append("tenantId", tenantId);
      formData.append("type", dataType);

      console.log("========== BULK UPLOAD ==========");
      console.log("Tenant ID:", tenantId);
      console.log("Data Type:", dataType);
      console.log("File:", selectedFile);
      console.log("FormData:", formData);

      // Demo upload progress
      for (let progress = 10; progress <= 100; progress += 10) {
        await new Promise((resolve) => setTimeout(resolve, 100));

        setUploadProgress(progress);
      }

      toast.success(
        `${getDataTypeLabel(dataType)} data uploaded successfully!`,
        {
          position: "top-right",
          autoClose: 1500,
        },
      );

      // Reset file after successful upload
      setSelectedFile(null);
      setUploadProgress(0);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      // If you want to close modal automatically:
      // onClose();
    } catch (error) {
      console.error("Bulk upload failed:", error);

      toast.error("Bulk upload failed. Please try again.", {
        position: "top-right",
        autoClose: 1500,
      });
    } finally {
      setIsUploading(false);
    }
  };

  // --------------------------------------------------
  // DOWNLOAD CSV TEMPLATE
  // --------------------------------------------------
  const downloadTemplate = () => {
    let headers = [];

    if (dataType === "player") {
      headers = [
        "firstName",
        "lastName",
        "email",
        "phone",
        "dateOfBirth",
        "gender",
      ];
    }

    if (dataType === "coach") {
      headers = [
        "firstName",
        "lastName",
        "email",
        "phone",
        "specialization",
        "experience",
      ];
    }

    if (dataType === "staff") {
      headers = ["firstName", "lastName", "email", "phone", "role"];
    }

    const csvContent = headers.join(",") + "\n";

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");

    link.href = url;

    link.download = `${dataType}-bulk-upload-template.csv`;

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);

    URL.revokeObjectURL(url);

    toast.success("Template downloaded.", {
      position: "top-right",
      autoClose: 1000,
    });
  };

  // --------------------------------------------------
  // FILE SIZE
  // --------------------------------------------------
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";

    const sizes = ["Bytes", "KB", "MB", "GB"];

    const index = Math.floor(Math.log(bytes) / Math.log(1024));

    return (
      Math.round((bytes / Math.pow(1024, index)) * 100) / 100 +
      " " +
      sizes[index]
    );
  };

  // --------------------------------------------------
  // DATA TYPE OPTIONS
  // --------------------------------------------------
  const dataTypes = [
    {
      value: "player",
      label: "Players",
      icon: "fa fa-user",
      description: "Upload player records",
    },
    {
      value: "coach",
      label: "Coaches",
      icon: "fa fa-user-tie",
      description: "Upload coach records",
    },
    {
      value: "staff",
      label: "Staff",
      icon: "fa fa-users",
      description: "Upload staff records",
    },
  ];

  return (
    <Modal show={show} onHide={onClose} centered size="lg" backdrop="static">
      {/* ==================================================
          HEADER
      ================================================== */}

      <Modal.Header closeButton className="border-bottom">
        <div>
          <Modal.Title className="fw-semibold">Bulk Data Upload</Modal.Title>

          <small className="text-muted">
            Upload coaches, players or staff using Excel / CSV
          </small>
        </div>
      </Modal.Header>

      {/* ==================================================
          BODY
      ================================================== */}

      <Modal.Body className="px-4 py-4">
        {/* ==================================================
            TENANT INFORMATION
        ================================================== */}

        <div
          className="mb-4 p-3 rounded-3"
          style={{
            background: "#f8f9fa",
            border: "1px solid #e9ecef",
          }}
        >
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <small className="text-muted d-block">Tenant</small>

              <strong>{tenant?.name || "Current Academy"}</strong>
            </div>

            <div className="text-end">
              <small className="text-muted d-block">Tenant ID</small>

              <span className="fw-medium">{tenantId || "N/A"}</span>
            </div>
          </div>
        </div>

        {/* ==================================================
            DATA TYPE
        ================================================== */}

        <Form.Group className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <Form.Label className="fw-semibold mb-0">
              Select Data Type
            </Form.Label>

            {selectedFile && (
              <small className="text-muted">
                Remove the file to change type
              </small>
            )}
          </div>

          <div className="row g-3">
            {dataTypes.map((item) => {
              const isSelected = dataType === item.value;

              // Once file is selected, disable changing type
              const isDisabled = selectedFile !== null || isUploading;

              return (
                <div className="col-md-4" key={item.value}>
                  <div
                    onClick={() => {
                      // IMPORTANT:
                      // Do not change type when file exists
                      if (!isDisabled) {
                        setDataType(item.value);
                      }
                    }}
                    className="h-100 p-3 rounded-3"
                    style={{
                      cursor: isDisabled ? "not-allowed" : "pointer",

                      opacity: isDisabled && !isSelected ? 0.55 : 1,

                      border: isSelected
                        ? "2px solid #0d6efd"
                        : "1px solid #dee2e6",

                      background: isSelected ? "#f0f6ff" : "#fff",

                      transition: "all 0.2s ease",

                      pointerEvents: isDisabled ? "none" : "auto",
                    }}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <div
                        className="d-flex align-items-center justify-content-center rounded-circle"
                        style={{
                          width: 42,
                          height: 42,

                          background: isSelected ? "#0d6efd" : "#f1f3f5",

                          color: isSelected ? "#fff" : "#495057",
                        }}
                      >
                        <i className={item.icon} />
                      </div>

                      <div>
                        <div className="fw-semibold">{item.label}</div>

                        <small className="text-muted">{item.description}</small>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Form.Group>

        {/* ==================================================
            SELECTED TYPE
        ================================================== */}

        {selectedFile && (
          <div className="mb-3">
            <div
              className="d-flex align-items-center gap-2 p-2 px-3 rounded-3"
              style={{
                background: "#e9f2ff",
                border: "1px solid #b6d4fe",
              }}
            >
              <i className="fa fa-info-circle text-primary" />

              <div>
                <small className="text-muted">Uploading as</small>

                <div className="fw-semibold text-primary">
                  {getDataTypeLabel(dataType)}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ==================================================
            FILE UPLOAD AREA
        ================================================== */}

        {!selectedFile ? (
          <div
            onClick={() => fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className="text-center rounded-3 p-5"
            style={{
              cursor: "pointer",

              border: isDragging ? "2px dashed #0d6efd" : "2px dashed #ced4da",

              background: isDragging ? "#f0f6ff" : "#fafafa",

              transition: "all 0.2s ease",
            }}
          >
            <div
              className="d-flex align-items-center justify-content-center rounded-circle mx-auto mb-3"
              style={{
                width: 60,
                height: 60,
                background: "#e9f2ff",
                color: "#0d6efd",
              }}
            >
              <i
                className="fa fa-cloud-upload-alt"
                style={{
                  fontSize: 26,
                }}
              />
            </div>

            <h6 className="fw-semibold mb-2">
              Drop your file here or click to browse
            </h6>

            <p className="text-muted small mb-2">
              Upload {getDataTypeLabel(dataType).toLowerCase()} data in Excel or
              CSV format
            </p>

            <span className="text-muted small">
              Supported: .xlsx, .xls, .csv · Max 10 MB
            </span>

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleInputChange}
              style={{
                display: "none",
              }}
            />
          </div>
        ) : (
          /* ==================================================
             SELECTED FILE
          ================================================== */

          <div
            className="p-3 rounded-3"
            style={{
              border: "1px solid #dee2e6",
              background: "#f8f9fa",
            }}
          >
            <div className="d-flex align-items-center">
              <div
                className="d-flex align-items-center justify-content-center rounded-3 me-3"
                style={{
                  width: 48,
                  height: 48,
                  background: "#e9f2ff",
                  color: "#0d6efd",
                }}
              >
                <i
                  className="fa fa-file-excel"
                  style={{
                    fontSize: 22,
                  }}
                />
              </div>

              <div className="flex-grow-1">
                <div className="fw-semibold">{selectedFile.name}</div>

                <small className="text-muted">
                  {formatFileSize(selectedFile.size)}
                </small>
              </div>

              {/* Remove File */}
              <Button
                variant="link"
                className="text-danger p-2"
                onClick={removeFile}
                disabled={isUploading}
                title="Remove file"
              >
                <i className="fa fa-trash" />
              </Button>
            </div>

            {/* Upload Progress */}
            {isUploading && (
              <div className="mt-3">
                <div className="d-flex justify-content-between mb-1">
                  <small className="text-muted">Uploading...</small>

                  <small className="text-muted">{uploadProgress}%</small>
                </div>

                <ProgressBar now={uploadProgress} animated />
              </div>
            )}
          </div>
        )}

        {/* ==================================================
            TEMPLATE
        ================================================== */}

        <div className="d-flex justify-content-between align-items-center mt-3">
          <small className="text-muted">
            Don't have a template? Download one for{" "}
            <strong>{getDataTypeLabel(dataType).toLowerCase()}</strong>.
          </small>

          <Button
            variant="link"
            className="p-0 text-decoration-none"
            onClick={downloadTemplate}
            disabled={isUploading}
          >
            <i className="fa fa-download me-1" />
            Download Template
          </Button>
        </div>

        {/* ==================================================
            INFO
        ================================================== */}

        <div
          className="mt-4 p-3 rounded-3"
          style={{
            background: "#fff8e6",
            border: "1px solid #ffe69c",
          }}
        >
          <div className="d-flex gap-2">
            <i className="fa fa-info-circle text-warning mt-1" />

            <div>
              <div className="fw-semibold small">Before uploading</div>

              <small className="text-muted">
                Make sure your file contains the correct column names and valid
                data. Duplicate or invalid records may be skipped during
                processing.
              </small>
            </div>
          </div>
        </div>
      </Modal.Body>

      {/* ==================================================
          FOOTER
      ================================================== */}

      <Modal.Footer className="border-top px-4 py-3">
        <Button
          variant="light"
          className="px-4 rounded-pill"
          onClick={onClose}
          disabled={isUploading}
        >
          Cancel
        </Button>

        <Button
          variant="primary"
          className="px-4 rounded-pill"
          onClick={handleUpload}
          disabled={!selectedFile || isUploading || !tenantId}
        >
          {isUploading ? (
            <>
              <i className="fa fa-spinner fa-spin me-2" />
              Uploading...
            </>
          ) : (
            <>
              <i className="fa fa-upload me-2" />
              Upload {getDataTypeLabel(dataType)}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UploadBulkDataModal;
